import { ExcelPlanResponse, FilesService, ProductionPlanService } from "@/api";
import { useTaskWithProgress } from "@/api/hooks/useTaskWithProgress";
import ResultWithProgress from "@/components/ResultWithProgress";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "primereact/button";
import ItemMasterSummary from "../components/ItemMasterSummary";
import OrderSummary from "../components/OrderSummary";
import ProductionTaskSummary from "../components/ProductionTaskSummary";
import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { toastAtom } from "@/atoms/toastAtom";


interface LoaderProps {
  onLoad(data: ExcelPlanResponse): void;
}


const Loader : React.FC<LoaderProps> = ({ onLoad }) => {
  const navigate = useNavigate()
  const [reloadCount, setReloadCount] = useState(0)
  const [isReloadingSource, setIsReloadingSource] = useState(false)
  const toast = useAtomValue(toastAtom)
  const {
    isLoading,
    isFetched,
    progress,
    lastLog,
    logs,
    isError,
    error,
    data: createPlanResult,
    refetch
  } = useTaskWithProgress(
    ()=>ProductionPlanService.createProductionPlan(reloadCount > 0 ? 1: 0),
    ['createPlan',reloadCount],
    {}
  )

  const echoProducts = useMemo(() => createPlanResult?.echoOrderProducts ?? [], [createPlanResult])
  const echoOrders = useMemo(() => createPlanResult?.echoOrders ?? [], [createPlanResult])
  const echoProductionTasks = useMemo(() => createPlanResult?.echoProductionTasks ?? [], [createPlanResult])

  const handleProceed = () => {
    if(isError || !createPlanResult){
      return
    }
    onLoad({
      ...createPlanResult,
      echoProducts,
      echoOrders,
    } satisfies ExcelPlanResponse)
  }


  const handleReload = async () => {
    const onedriveFileId = createPlanResult?.fileMeta?.onedrive_file_id

    if (!onedriveFileId) {
      setReloadCount(prev => prev + 1)
      return
    }

    try {
      setIsReloadingSource(true)
      await FilesService.downloadOnedriveFile({ file_id: onedriveFileId })
      setReloadCount(prev => prev + 1)
    } catch (err) {
      console.error('OneDrive reload failed', err)
      toast?.show({
        severity: 'error',
        summary: '再読込エラー',
        detail: 'OneDriveからファイルを再取得できませんでした。',
        life: 4000,
      })
    } finally {
      setIsReloadingSource(false)
    }
  }

  useEffect(() => {
    if (reloadCount > 0) {
      refetch()
    }
  }, [reloadCount, refetch])

  return (
    <div className="mt-6">
      <ResultWithProgress
        isObserving={isLoading}
        progress={progress}
        lastLog={lastLog}
        logs={logs}
        isError={isError}
        error={error}>
          <ItemMasterSummary itemMaster={echoProducts ??  []} />
          <OrderSummary orders={echoOrders ?? []} />
          <ProductionTaskSummary tasks={echoProductionTasks ?? []} />
          <p className="mx-auto text-center mt-4">
            Excelファイルを読み込みました。統計を表示しています。
          </p>
        </ResultWithProgress>
      <div className="flex flex-row justify-content-between mt-6">
        <Button label="戻る" icon="pi pi-arrow-left"
          iconPos="left" size="small" severity='secondary'
          onClick={() => { navigate({
            to: "/dashboard",
          })}} />
          {isFetched && (
            (createPlanResult?.fileMeta?.onedrive_file_id && (
              <Button
                label="再読込"
                icon="pi pi-sync"
                severity="warning"
                loading={isLoading || isReloadingSource}
                disabled={isLoading || isReloadingSource}
                onClick={handleReload}
              />
            ))
          )}
        <Button label="進む" icon="pi pi-arrow-right"
          iconPos="right" size="small" severity='success'
          disabled={isLoading || isError || echoProducts.length === 0}
          onClick={handleProceed} />
      </div>
    </div>
  )
}
export default Loader
